<template>
  <div>
    <b-card class="health-assessments-list-card donors-list-card">
      <div class="gradient-line-wrapper"></div>
      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0">Donors</h5>
          <small class="text-muted"
            >Manage donor records and contact details</small
          >
        </div>
      </div>

      <b-row class="align-items-center filter-row-single-line mb-2 no-wrap-row">
        <b-col xl="5" lg="5" md="6" sm="12" cols="12">
          <b-form-input
            v-model="searchQuery"
            placeholder="Search by name, email, country, WhatsApp, student IDs, notes…"
            @input="onSearchInput"
            class="filter-control"
            debounce="400"
          />
        </b-col>
        <b-col xl="7" lg="7" md="6" sm="12" cols="12" class="text-right">
          <div
            class="d-flex align-items-center justify-content-end action-buttons-group"
          >
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              @click="openAddModal"
              class="action-btn-filter"
              :disabled="dataLoading"
            >
              <b-spinner v-if="dataLoading" small class="mr-1" />
              <feather-icon v-else icon="PlusIcon" size="16" class="mr-1" />
              Add Donor
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Pagination (no toggle; matches inHouseDoctor except checkbox) -->
      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-3 flex-wrap"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              :key="'page-size-' + perPage"
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
              @option:selected="closePageSizeDropdown"
              ref="pageSizeSelect"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @change="onPageChange"
          />
        </div>
      </div>

      <div class="table-scroll-container">
        <div
          class="table-scrollbar-top"
          ref="topScrollbar"
          @scroll="syncScroll"
        >
          <div class="table-scrollbar-content"></div>
        </div>

        <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
          <b-table
            class="health-assessments-table donors-table"
            :items="displayedDonors"
            :fields="fields"
            :busy="dataLoading"
            :responsive="false"
            hover
            show-empty
            empty-text="No donors found"
          >
            <template #table-busy>
              <tbody>
                <tr v-for="n in 6" :key="'skeleton-' + n" class="skeleton-row">
                  <td
                    v-for="(field, index) in fields"
                    :key="'skeleton-cell-' + index"
                    class="skeleton-cell"
                  >
                    <div class="skeleton-loader"></div>
                  </td>
                </tr>
              </tbody>
            </template>

            <template #cell(sno)="{ index }">
              <span class="text-muted">{{
                (currentPage - 1) * perPage + index + 1
              }}</span>
            </template>

            <template #cell(photo)="{ item }">
              <div class="d-flex align-items-center">
                <b-img
                  v-if="item.donor && item.donor.photo"
                  :src="item.donor.photo"
                  rounded
                  width="40"
                  height="40"
                  class="object-fit-cover"
                />
                <div
                  v-else
                  class="d-flex align-items-center justify-content-center bg-light rounded"
                  style="width: 40px; height: 40px"
                >
                  <feather-icon icon="UserIcon" size="18" class="text-muted" />
                </div>
              </div>
            </template>

            <template #cell(name)="{ item }">
              <div class="font-weight-bold text-nowrap">
                {{ (item.donor && item.donor.firstName) || "—" }}
                {{ (item.donor && item.donor.surname) || "" }}
              </div>
              <div class="text-muted small text-nowrap">
                {{ (item.donor && item.donor.email) || "" }}
              </div>
            </template>

            <template #cell(studentCount)="{ item }">
              <b-badge variant="light-primary" class="font-weight-bold">
                {{ item.studentCount || 0 }}
              </b-badge>
            </template>

            <template #cell(dateOfBirth)="{ item }">
              <span>{{ formatDob(item.donor && item.donor.dateOfBirth) }}</span>
            </template>

            <template #cell(countryOfOrigin)="{ item }">
              <span>{{
                (item.donor && item.donor.countryOfOrigin) || "—"
              }}</span>
            </template>

            <template #cell(countryOfResidence)="{ item }">
              <span>{{
                (item.donor && item.donor.countryOfResidence) || "—"
              }}</span>
            </template>

            <template #cell(whatsappNumber)="{ item }">
              <span>{{
                (item.donor && item.donor.whatsappNumber) || "—"
              }}</span>
            </template>

            <template #cell(email)="{ item }">
              <span>{{ (item.donor && item.donor.email) || "—" }}</span>
            </template>

            <template #cell(notes)="{ item }">
              <div
                class="text-truncate"
                style="max-width: 200px"
                :title="(item.donor && item.donor.notes) || ''"
              >
                {{ (item.donor && item.donor.notes) || "—" }}
              </div>
            </template>

            <template #cell(status)="{ item }">
              <b-badge
                :variant="
                  ((item.donor && item.donor.status) || '').toLowerCase() ===
                  'active'
                    ? 'success'
                    : 'secondary'
                "
              >
                {{ (item.donor && item.donor.status) || "—" }}
              </b-badge>
            </template>

            <template #cell(actions)="{ item }">
              <div class="d-flex align-items-center justify-content-end">
                <b-button
                  v-if="rights.edit"
                  variant="gradient-primary"
                  size="sm"
                  v-b-tooltip.hover.left
                  title="Edit"
                  class="action-btn-table"
                  @click="openEditModal(item.donor)"
                  :disabled="dataLoading"
                >
                  <feather-icon icon="EditIcon" size="16" />
                </b-button>
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </b-card>

    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Donor' : 'Add Donor'"
      size="lg"
      @hidden="resetForm"
      centered
      no-close-on-backdrop
    >
      <b-form @submit.prevent class="mt-1">
        <div class="form-card soft-card">
          <b-row>
            <b-col md="12">
              <b-form-group label="Photo">
                <div
                  class="d-flex align-items-center flex-wrap donor-photo-row"
                >
                  <div class="position-relative mr-2 mb-1">
                    <b-link
                      class="d-inline-block"
                      @click.prevent="triggerDonorPhotoInput"
                    >
                      <div
                        v-if="photoUploading"
                        class="donor-photo-wrap d-flex align-items-center justify-content-center"
                      >
                        <b-spinner type="grow" variant="primary" small />
                      </div>
                      <b-img
                        v-else-if="form.photo"
                        :src="form.photo"
                        width="96"
                        height="96"
                        rounded
                        class="border donor-photo-preview object-fit-cover"
                        alt="Donor photo"
                      />
                      <div
                        v-else
                        class="donor-photo-wrap d-flex flex-column align-items-center justify-content-center text-muted"
                      >
                        <feather-icon icon="ImageIcon" size="28" />
                        <small class="mt-1 d-block">Click to upload</small>
                      </div>
                    </b-link>
                    <input
                      ref="donorPhotoInput"
                      type="file"
                      hidden
                      accept="image/*"
                      @change="onDonorPhotoSelected"
                    />
                  </div>
                  <div class="d-flex flex-column align-items-start">
                    <b-button
                      type="button"
                      size="sm"
                      variant="primary"
                      class="mb-50"
                      :disabled="photoUploading"
                      @click="triggerDonorPhotoInput"
                    >
                      <feather-icon icon="UploadIcon" size="14" class="mr-50" />
                      Choose image
                    </b-button>
                    <b-button
                      v-if="form.photo"
                      type="button"
                      size="sm"
                      variant="outline-danger"
                      :disabled="photoUploading"
                      @click="clearDonorPhoto"
                    >
                      <feather-icon icon="Trash2Icon" size="14" class="mr-50" />
                      Remove
                    </b-button>
                  </div>
                </div>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label-for="firstName">
                <template #label
                  >First name <span class="text-danger">*</span></template
                >
                <b-form-input
                  id="firstName"
                  v-model.trim="form.firstName"
                  placeholder="First name"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label-for="surname">
                <template #label
                  >Surname <span class="text-danger">*</span></template
                >
                <b-form-input
                  id="surname"
                  v-model.trim="form.surname"
                  placeholder="Surname"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label-for="dateOfBirth">
                <template #label
                  >Date of birth <span class="text-danger">*</span></template
                >
                <flat-pickr
                  id="dateOfBirth"
                  v-model="form.dateOfBirth"
                  :config="dobConfig"
                  class="form-control input-compact"
                  placeholder="Select date of birth"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label-for="countryOfOrigin">
                <template #label
                  >Country of origin
                  <span class="text-danger">*</span></template
                >
                <b-form-input
                  id="countryOfOrigin"
                  v-model.trim="form.countryOfOrigin"
                  placeholder="Country of origin"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                label="Country of residence"
                label-for="countryOfResidence"
              >
                <b-form-input
                  id="countryOfResidence"
                  v-model.trim="form.countryOfResidence"
                  placeholder="Country of residence"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label-for="whatsapp">
                <template #label
                  >WhatsApp number <span class="text-danger">*</span></template
                >
                <vue-tel-input
                  v-model="form.whatsappNumber"
                  :inputOptions="whatsappInputOpts"
                  :dropdownOptions="telDropOptions"
                  :defaultCountry="$store.state.countryCode"
                  @input="onWhatsappInput"
                />
                <small class="text-danger" v-if="whatsappError"
                  >Please enter a valid WhatsApp number</small
                >
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Email" label-for="email">
                <b-form-input
                  id="email"
                  v-model.trim="form.email"
                  type="email"
                  autocomplete="email"
                  placeholder="Email"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Notes" label-for="notes">
                <b-form-textarea
                  id="notes"
                  v-model.trim="form.notes"
                  rows="3"
                  placeholder="Notes"
                  class="input-compact"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                label="Students (Sponsored)"
                label-for="studentSearch"
              >
                <div class="search-section">
                  <b-form-input
                    id="studentSearch"
                    v-model="studentSearchQuery"
                    placeholder="Search students to add..."
                    @input="onStudentSearchChange"
                    class="search-input input-compact"
                  />
                </div>

                <div
                  v-if="studentSearchQuery.length >= 2"
                  class="search-results"
                >
                  <div
                    v-if="filteredStudentOptions.length > 0"
                    class="student-options"
                  >
                    <div
                      v-for="student in filteredStudentOptions"
                      :key="student.id"
                      class="student-option"
                      @click="selectStudent(student)"
                    >
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <strong>{{ student.name }}</strong>
                          <div class="text-muted small">
                            {{ student.className
                            }}{{
                              student.sectionName
                                ? " - " + student.sectionName
                                : ""
                            }}
                            <span v-if="student.grNo" class="ml-2"
                              >GR: {{ student.grNo }}</span
                            >
                          </div>
                        </div>
                        <feather-icon
                          icon="PlusCircleIcon"
                          size="16"
                          class="text-primary"
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="
                      filteredStudentOptions.length === 0 &&
                      !studentSearchLoading
                    "
                    class="empty-state"
                  >
                    <feather-icon icon="SearchIcon" size="32" />
                    <p>No students found</p>
                  </div>

                  <div v-if="studentSearchLoading" class="loading-state">
                    <b-spinner variant="primary" size="sm" />
                    <span>Searching...</span>
                  </div>
                </div>

                <div
                  v-if="form.selectedStudents.length > 0"
                  class="selected-students-list mt-50"
                >
                  <div
                    v-for="std in form.selectedStudents"
                    :key="std.id"
                    class="selected-student mb-50"
                  >
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <strong>{{ std.name }}</strong>
                        <div class="text-muted small">
                          {{ std.className
                          }}{{ std.sectionName ? " - " + std.sectionName : "" }}
                          <span v-if="std.grNo" class="ml-2"
                            >GR: {{ std.grNo }}</span
                          >
                        </div>
                      </div>
                      <b-button
                        variant="outline-danger"
                        size="sm"
                        @click="removeSelectedStudent(std.id)"
                      >
                        <feather-icon icon="XIcon" size="14" />
                      </b-button>
                    </div>
                  </div>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </b-form>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false">
          Cancel
        </b-button>
        <b-button variant="primary" @click="saveDonor" :disabled="saving">
          <b-spinner v-if="saving" small />
          {{ isEdit ? "Update" : "Save" }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BFormTextarea,
  BButton,
  BTable,
  BImg,
  BBadge,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BSpinner,
  VBTooltip,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mapActions } from "vuex";
import { getUploadPath, uploadToMySkool } from "@/utils/gdriveUpload";
import axios from "axios";

export default {
  name: "Donors",
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BFormTextarea,
    BButton,
    BTable,
    BImg,
    BBadge,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BSpinner,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rights: {},
      searchQuery: "",
      searchDebounce: null,
      donors: [],
      dataLoading: false,
      saving: false,
      showModal: false,
      isEdit: false,
      currentPage: 1,
      perPage: 50,
      totalCount: 0,
      totalPages: 1,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "30", value: 30 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      photoUploading: false,
      pendingStatus: "active",
      studentSearchQuery: "",
      filteredStudentOptions: [],
      studentSearchLoading: false,
      studentSearchTimer: null,
      studentSearchCache: {},
      lastStudentSearchToken: 0,
      pendingDonor: null,
      fields: [
        { key: "sno", label: "S.No", thClass: "w-sn" },
        { key: "photo", label: "Photo", thClass: "w-photo" },
        { key: "name", label: "Name", sortable: false },
        { key: "dateOfBirth", label: "Date of birth", sortable: false },
        { key: "countryOfOrigin", label: "Origin", sortable: false },
        { key: "countryOfResidence", label: "Residence", sortable: false },
        { key: "whatsappNumber", label: "WhatsApp", sortable: false },
        // { key: "email", label: "Email", sortable: false },
        // { key: "notes", label: "Notes", sortable: false },
        // { key: "status", label: "Status", sortable: false },
        { key: "studentCount", label: "Students", sortable: false },
        { key: "actions", label: "Actions" },
      ],
      form: {
        id: 0,
        firstName: "",
        surname: "",
        photo: "",
        dateOfBirth: null,
        countryOfOrigin: "",
        countryOfResidence: "",
        whatsappNumber: "",
        email: "",
        notes: "",
        selectedStudents: [],
      },
      dobConfig: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
        placeholder: "Select date of birth",
      },
      whatsappError: false,
      whatsappInputOpts: { placeholder: "Enter WhatsApp number" },
      telDropOptions: {
        disabled: false,
        showDialCodeInList: true,
        showFlags: true,
      },
    };
  },
  computed: {
    displayedRange() {
      if (this.totalCount === 0) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    displayedDonors() {
      const list = this.donors || [];
      const q = (this.searchQuery || "").trim().toLowerCase();
      if (!q) return list;
      return list.filter((row) => {
        const d = row.donor || {};
        const haystack = [
          d.firstName,
          d.surname,
          d.sIDs,
          d.countryOfOrigin,
          d.countryOfResidence,
          d.whatsappNumber,
          d.email,
          d.notes,
          d.status,
        ];
        return haystack.some((f) => f && String(f).toLowerCase().includes(q));
      });
    },
  },
  created() {
    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({
        name: "misc-not-authorized",
      });
    } else {
      this.rights = this.$store.state.rights[this.$route.name];
      this.loadDonors();
    }
  },
  mounted() {
    this.$nextTick(() => this.updateScrollbarWidth());
    window.addEventListener("resize", this.updateScrollbarWidth);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateScrollbarWidth);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    buildLoadUrl() {
      return (
        this.$store.state.domain +
        "Donors/LoadData?db=" +
        this.$store.state.userData.db +
        "&cID=" +
        this.$store.state.userData.cId +
        "&page=" +
        this.currentPage +
        "&pageSize=" +
        this.perPage
      );
    },

    buildSaveUrl() {
      return (
        this.$store.state.domain +
        "Donors/Save?db=" +
        this.$store.state.userData.db +
        "&cID=" +
        this.$store.state.userData.cId +
        "&userID=" +
        this.$store.state.userData.userID
      );
    },

    formatDob(val) {
      if (val == null || val === "") return "—";
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    updateScrollbarWidth() {
      this.$nextTick(() => {
        const tableWrapper = this.$refs.tableWrapper;
        const topScrollbar = this.$refs.topScrollbar;
        const topContent =
          topScrollbar &&
          topScrollbar.querySelector(".table-scrollbar-content");
        if (tableWrapper && topContent) {
          topContent.style.width = `${tableWrapper.scrollWidth}px`;
          topScrollbar.scrollLeft = tableWrapper.scrollLeft;
        }
      });
    },

    syncScroll(event) {
      const tableWrapper = this.$refs.tableWrapper;
      const topScrollbar = this.$refs.topScrollbar;
      if (!tableWrapper || !topScrollbar) return;
      if (event && event.target === tableWrapper) {
        topScrollbar.scrollLeft = tableWrapper.scrollLeft;
      } else if (event && event.target === topScrollbar) {
        tableWrapper.scrollLeft = topScrollbar.scrollLeft;
      }
    },

    onSearchInput() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.updateScrollbarWidth();
      }, 200);
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadDonors();
    },

    closePageSizeDropdown() {
      if (
        this.$refs.pageSizeSelect &&
        this.$refs.pageSizeSelect.toggleDropdown
      ) {
        this.$refs.pageSizeSelect.toggleDropdown(false);
      }
    },

    onPageChange() {
      this.loadDonors();
    },

    async loadDonors() {
      this.dataLoading = true;
      try {
        const obj = {
          url: this.buildLoadUrl(),
          token: this.$store.state.userData.token,
          returnData: true,
        };
        const response = await this.get(obj);
        if (response === false) {
          this.donors = [];
          this.totalCount = 0;
          this.$bvToast.toast("Failed to load donors.", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
          return;
        }
        const payload =
          response && typeof response === "object" ? response : {};
        const container = payload.data !== undefined ? payload.data : payload;
        let records = [];
        if (Array.isArray(container?.data)) {
          records = container.data;
        } else if (Array.isArray(container)) {
          records = container;
        } else if (Array.isArray(payload)) {
          records = payload;
        } else if (Array.isArray(payload.data)) {
          records = payload.data;
        }

        const meta = {
          totalCount:
            payload?.totalCount ??
            container?.totalCount ??
            payload?.data?.totalCount,
          page: payload?.page ?? container?.page,
          pageSize: payload?.pageSize ?? container?.pageSize,
          totalPages: payload?.totalPages ?? container?.totalPages,
        };

        this.donors = records || [];
        const pageSizeFromApi = Number(meta.pageSize) || this.perPage;
        const totalFromApi = Number(meta.totalCount);
        const totalRecords =
          !Number.isNaN(totalFromApi) && totalFromApi >= 0
            ? totalFromApi
            : records.length;
        this.totalCount = totalRecords;
        if (!Number.isNaN(Number(meta.page)) && Number(meta.page) > 0) {
          this.currentPage = Number(meta.page);
        }
        this.perPage = pageSizeFromApi;
        this.totalPages =
          Number(meta.totalPages) ||
          Math.max(1, Math.ceil(this.totalCount / (this.perPage || 1)));

        this.$nextTick(() => this.updateScrollbarWidth());
      } catch (e) {
        console.error("Load donors error:", e);
        this.donors = [];
        this.totalCount = 0;
        this.$bvToast.toast("Failed to load donors.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.dataLoading = false;
      }
    },

    openAddModal() {
      this.isEdit = false;
      this.pendingStatus = "active";
      this.resetForm();
      this.showModal = true;
    },

    async openEditModal(item) {
      this.isEdit = true;
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];

      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Donors/LoadDonorStudents?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&donorID=" +
            (Number(item.id) || 0),
          token: this.$store.state.userData.token,
          returnData: true,
        };
        const response = await this.get(obj);
        const donor = response && response.donor;

        if (!donor) {
          this.$bvToast.toast("Failed to load donor details.", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
          return;
        }

        this.pendingDonor = donor;
        this.pendingStatus = (donor.status || "active").toString();
        const students = response.students || [];
        this.form = {
          id: Number(donor.id) || 0,
          firstName: donor.firstName || "",
          surname: donor.surname || "",
          photo: donor.photo || "",
          dateOfBirth: donor.dateOfBirth || null,
          countryOfOrigin: donor.countryOfOrigin || "",
          countryOfResidence: donor.countryOfResidence || "",
          whatsappNumber: donor.whatsappNumber || "",
          email: donor.email || "",
          notes: donor.notes || "",
          selectedStudents: students.map((s) => {
            let className = "";
            let sectionName = "";
            if (s.meta_class) {
              const parts = s.meta_class.split(" - ");
              className = parts[0] ? parts[0].trim() : "";
              sectionName = parts[1] ? parts[1].trim() : "";
            }
            return {
              id: s.id,
              name: s.name || "Student " + s.id,
              className,
              sectionName,
              grNo: s.grNo || "",
            };
          }),
        };
        this.showModal = true;
      } catch (error) {
        this.$bvToast.toast("Failed to load donor details.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      }
    },

    triggerDonorPhotoInput() {
      if (this.photoUploading) return;
      const el = this.$refs.donorPhotoInput;
      if (el) el.click();
    },

    async onDonorPhotoSelected() {
      const input = this.$refs.donorPhotoInput;
      if (!input || !input.files || !input.files[0]) return;
      this.photoUploading = true;
      try {
        const uploaded = await uploadToMySkool([input.files[0]]);
        const path = getUploadPath(uploaded[0]);
        if (path) {
          this.form.photo = path;
          this.$bvToast.toast("Photo uploaded.", {
            title: "Success",
            variant: "success",
            solid: true,
          });
        } else {
          this.$bvToast.toast("Could not read upload path from response.", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
        }
      } catch (err) {
        console.error(err);
        this.$bvToast.toast("Photo upload failed.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.photoUploading = false;
        input.value = "";
      }
    },

    clearDonorPhoto() {
      this.form.photo = "";
      const input = this.$refs.donorPhotoInput;
      if (input) input.value = "";
    },

    buildSaveBody() {
      const statusOut = this.isEdit ? this.pendingStatus || "active" : "active";
      const sIDs = this.form.selectedStudents.map((s) => s.id).join(",");
      const base =
        this.isEdit && this.pendingDonor ? { ...this.pendingDonor } : {};
      return {
        ...base,
        id: this.form.id || 0,
        campusID: this.isEdit
          ? (this.pendingDonor && this.pendingDonor.campusID) ||
            this.$store.state.userData.cId
          : this.$store.state.userData.cId,
        firstName: this.form.firstName || "",
        surname: this.form.surname || "",
        photo: this.form.photo || "",
        sIDs,
        dateOfBirth: this.form.dateOfBirth || null,
        countryOfOrigin: this.form.countryOfOrigin || "",
        countryOfResidence: this.form.countryOfResidence || "",
        whatsappNumber: this.form.whatsappNumber || "",
        email: this.form.email || "",
        notes: this.form.notes || "",
        status: statusOut,
      };
    },

    onWhatsappInput(value, phoneObj) {
      if (this.form.whatsappNumber) {
        if (phoneObj && phoneObj.valid) {
          this.whatsappError = false;
          this.form.whatsappNumber = phoneObj.number;
        } else {
          this.whatsappError = true;
        }
      } else {
        this.whatsappError = false;
      }
    },

    onStudentSearchChange() {
      if (this.studentSearchTimer) clearTimeout(this.studentSearchTimer);
      this.studentSearchTimer = setTimeout(() => {
        this.performStudentSearch();
      }, 700);
    },

    async performStudentSearch() {
      const term = (this.studentSearchQuery || "").trim();
      if (term.length < 2) {
        this.filteredStudentOptions = [];
        return;
      }
      if (this.studentSearchCache[term]) {
        this.filteredStudentOptions = this.studentSearchCache[term];
        return;
      }
      const token = ++this.lastStudentSearchToken;
      this.studentSearchLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Donors/SearchStudents?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&keyword=" +
            encodeURIComponent(term) +
            "&type=",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        if (token !== this.lastStudentSearchToken) return;
        let studentsData = [];
        if (response && response.data && Array.isArray(response.data)) {
          studentsData = response.data;
        } else if (Array.isArray(response)) {
          studentsData = response;
        }
        const mapped = studentsData.map((s) => {
          let className = "";
          let sectionName = "";
          if (s.meta_class) {
            const parts = s.meta_class.split(" - ");
            className = parts[0] ? parts[0].trim() : "";
            sectionName = parts[1] ? parts[1].trim() : "";
          }
          return {
            id: s.id,
            name: s.name || "Unknown Student",
            className,
            sectionName,
            grNo: s.grNo || "",
          };
        });
        this.filteredStudentOptions = mapped;
        this.studentSearchCache[term] = mapped;
      } catch (error) {
        this.filteredStudentOptions = [];
      } finally {
        if (token === this.lastStudentSearchToken) {
          this.studentSearchLoading = false;
        }
      }
    },

    selectStudent(student) {
      const alreadyAdded = this.form.selectedStudents.some(
        (s) => s.id === student.id
      );
      if (!alreadyAdded) {
        this.form.selectedStudents.push(student);
      }
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
    },

    removeSelectedStudent(id) {
      this.form.selectedStudents = this.form.selectedStudents.filter(
        (s) => s.id !== id
      );
    },

    async saveDonor() {
      const warn = (msg) => {
        this.$bvToast.toast(msg, {
          title: "Validation",
          variant: "warning",
          solid: true,
        });
      };
      if (!this.form.firstName || !String(this.form.firstName).trim()) {
        return warn("First name is required.");
      }
      if (!this.form.surname || !String(this.form.surname).trim()) {
        return warn("Surname is required.");
      }
      if (!this.form.dateOfBirth) {
        return warn("Date of birth is required.");
      }
      if (
        !this.form.countryOfOrigin ||
        !String(this.form.countryOfOrigin).trim()
      ) {
        return warn("Country of origin is required.");
      }
      if (
        !this.form.whatsappNumber ||
        !String(this.form.whatsappNumber).trim()
      ) {
        return warn("WhatsApp number is required.");
      }
      if (this.whatsappError) {
        return warn("Please enter a valid WhatsApp number.");
      }
      this.saving = true;
      try {
        const body = this.buildSaveBody();
        const obj = {
          url: this.buildSaveUrl(),
          body,
          message: this.isEdit
            ? "Donor updated successfully."
            : "Donor saved successfully.",
          context: this,
          token: this.$store.state.userData.token,
        };
        const ok = await this.post(obj);
        if (ok) {
          this.showModal = false;
          this.loadDonors();
        }
      } catch (e) {
        console.error("Save donor error:", e);
        this.$bvToast.toast("Could not save donor.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.saving = false;
      }
    },

    resetForm() {
      this.photoUploading = false;
      this.pendingStatus = "active";
      this.pendingDonor = null;
      this.whatsappError = false;
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      this.studentSearchLoading = false;
      this.form = {
        id: 0,
        firstName: "",
        surname: "",
        photo: "",
        dateOfBirth: null,
        countryOfOrigin: "",
        countryOfResidence: "",
        whatsappNumber: "",
        email: "",
        notes: "",
        selectedStudents: [],
      };
      this.$nextTick(() => {
        const input = this.$refs.donorPhotoInput;
        if (input) input.value = "";
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

/* Mirror in-house doctor list styling */
.health-assessments-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  position: relative;
  overflow: visible;
  z-index: 1;

  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0.75rem;
    right: 0.75rem;
    width: calc(100% - 1.5rem);
    height: 5px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    pointer-events: none;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
      border-radius: 10px 10px 0 0;
    }
  }
}

.table-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
  }

  small {
    font-size: 0.85rem;
    color: #6b7280;
  }
}

.filter-row-single-line {
  gap: 0.5rem;
}
.no-wrap-row {
  flex-wrap: nowrap;
  @media (max-width: 991.98px) {
    flex-wrap: wrap;
  }
}
.filter-control {
  height: 38px;
  border-radius: 8px;
  border: 1px solid #e0d8ff;
  font-size: 0.875rem;
}
.action-btn-filter {
  height: 38px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.875rem;
}
.action-buttons-group {
  gap: 0.5rem;
  flex-wrap: wrap;
}
.soft-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e8e5fb;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.08);
  margin-bottom: 0.75rem;
}
.input-compact {
  min-height: 40px;
  border-radius: 10px !important;
  border: 1.5px solid #e0d8ff !important;
  padding: 0.45rem 0.75rem !important;
  font-size: 0.95rem !important;
}
.pagination-controls-top {
  padding: 0.75rem 0;
  border-top: 1px solid #f0edff;
  border-bottom: 1px solid #f0edff;
  margin: 1rem 0;
  flex-wrap: wrap;
  gap: 1rem;
}
.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.page-size-label {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
  white-space: nowrap;
}
.page-size-select-compact {
  min-width: 80px;
}
.page-size-select-compact ::v-deep .vs__dropdown-toggle {
  height: 32px;
  padding: 0 0.5rem;
  border-radius: 6px;
  border: 1px solid #e0d8ff;
  font-size: 0.8125rem;
}
.pagination-count-display {
  flex: 1;
  text-align: center;
  .count-text {
    font-size: 0.8125rem;
    color: #6b7280;
    strong {
      color: #2d1b69;
    }
  }
}
.pagination-compact {
  margin: 0;
}
.table-scroll-container {
  position: relative;
  width: 100%;
}
.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;
}
.table-scrollbar-top .table-scrollbar-content {
  height: 1px;
}
.table-wrapper {
  overflow-x: auto;
  width: 100%;
}
.health-assessments-table {
  margin-top: 1rem;
  border-radius: 12px;
  min-width: 100%;
  width: max-content;
  border: 1px solid #e5e7eb;
}
.health-assessments-table ::v-deep th {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  font-weight: 700;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.75rem 0.875rem;
  white-space: nowrap;
  border-bottom: 1px solid #e2e8f0;
}
.health-assessments-table ::v-deep td {
  padding: 0.65rem 0.75rem;
  font-size: 0.85rem;
  vertical-align: middle;
}
.skeleton-loader {
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f1f5f9, #e2e8f0, #f1f5f9);
  background-size: 200% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
.action-btn-table {
  border-radius: 8px;
}
.object-fit-cover {
  object-fit: cover;
}

.donor-photo-wrap {
  width: 96px;
  height: 96px;
  border: 1.5px dashed #d0c9f0;
  border-radius: 12px;
  background: #f8f7ff;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  &:hover {
    border-color: #7c5dfa;
    background: #f0edff;
  }
}
.donor-photo-preview {
  display: block;
  cursor: pointer;
}
.donor-photo-row {
  gap: 0.5rem;
}
.w-sn {
  width: 56px;
}
.w-photo {
  width: 64px;
}

/* ── Student search ── */
.search-section {
  margin-bottom: 0;
}
.search-input {
  border-radius: 8px !important;
  border: 1.5px solid #e0d8ff !important;
  padding: 8px 12px;
  font-size: 14px;
  width: 100%;
  &:focus {
    border-color: #7c5dfa !important;
    box-shadow: 0 0 0 0.2rem rgba(124, 93, 250, 0.2) !important;
  }
}
.search-results {
  position: relative;
  z-index: 1000;
  background: #fff;
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(115, 103, 240, 0.12);
  max-height: 280px;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 6px;
}
.student-options {
  max-height: none;
  overflow: visible;
}
.student-option {
  padding: 10px 14px;
  border-bottom: 1px solid #f3f0ff;
  cursor: pointer;
  transition: background 0.15s ease, border-left 0.15s ease,
    transform 0.15s ease;
  overflow: visible;
  strong {
    font-size: 0.875rem;
    color: #2d1b69;
  }
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #f5f2ff;
    border-left: 3px solid #7c5dfa;
    transform: translateX(2px);
    box-shadow: 0 2px 8px rgba(124, 93, 250, 0.1);
  }
  &:active {
    background: #ede9ff;
    transform: translateX(1px);
  }
}
.empty-state {
  padding: 32px 20px;
  text-align: center;
  color: #9ca3af;
  p {
    margin-top: 10px;
    font-size: 13px;
  }
}
.loading-state {
  padding: 18px;
  text-align: center;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
}
.search-results::-webkit-scrollbar {
  width: 5px;
}
.search-results::-webkit-scrollbar-track {
  background: #f3f0ff;
  border-radius: 4px;
}
.search-results::-webkit-scrollbar-thumb {
  background: #c4b5fd;
  border-radius: 4px;
}
.search-results::-webkit-scrollbar-thumb:hover {
  background: #7c5dfa;
}
.search-results * {
  overflow: visible !important;
}
.student-options * {
  overflow: visible !important;
}

/* ── Selected students list ── */
.selected-students-list {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.selected-student {
  padding: 9px 12px;
  background: linear-gradient(135deg, #f5f2ff 0%, #ede9ff 100%);
  border: 1px solid #ddd6fe;
  border-left: 3px solid #7c5dfa;
  border-radius: 8px;
  strong {
    font-size: 0.875rem;
    color: #2d1b69;
  }
}
</style>
